import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listItems } from '../../api/resources';
import Field from '../ui/Field';
import DateInput from '../ui/DateInput';
import { COUNTRIES, STATES } from '../../utils/india';
import { fmtDate } from '../../utils/format';

// Build the flat form-state shape from a worker record returned by the API.
// The current /api/workers backend only persists a handful of columns
// (name, phone, role, status) but the ERP master screen needs the full set —
// this maps what's available today and defaults the rest so the form (and a
// future richer API) both work without changes here.
export function workerToFormState(worker) {
  if (!worker) {
    return {
      code: '',
      ledgerName: '',
      grade: '',
      role: '',
      // Which garments this person can make. Kept apart from their rate:
      // being able to sew a kurta is a fact about them, a price is an
      // agreement that is negotiated, expires and is replaced.
      skillItemIds: [],
      isActive: true,
      openingBalance: '',
      acGroup: 'Creditors (Workers)',
      add1: '',
      add2: '',
      mobNo: '',
      mobNo2: '',
      phNo: '',
      email: '',
      city: '',
      pincode: '',
      state: '',
      country: 'India',
      salaryType: '',
      vatNo: '',
      taxation: '',
      panNo: '',
      birthDate: '',
      anniversaryDate: '',
      acType: '',
      acNo: '',
      ifsc: '',
      micr: '',
      bankName: '',
      bankAdd: '',
      acPerson: '',
      acContact: '',
      acEmail: '',
    };
  }
  return {
    code: worker.code || worker.id?.toString() || '',
    ledgerName: worker.ledgerName || worker.name || '',
    grade: worker.grade || '',
    role: worker.role || '',
    skillItemIds: (worker.skills || []).map((s) => s.id),
    skillItemIds: (worker.skills || []).map((s) => s.id),
    isActive: worker.isActive != null ? worker.isActive : worker.status !== 'INACTIVE',
    openingBalance: worker.openingBalance?.toString() || '',
    acGroup: worker.acGroup || 'Creditors (Workers)',
    add1: worker.add1 || '',
    add2: worker.add2 || '',
    mobNo: worker.mobNo || worker.phone || '',
    mobNo2: worker.mobNo2 || '',
    phNo: worker.phNo || '',
    email: worker.email || '',
    city: worker.city || '',
    pincode: worker.pincode || '',
    state: worker.state || '',
    country: worker.country || 'India',
    salaryType: worker.salaryType || '',
    vatNo: worker.vatNo || worker.gstNo || '',
    taxation: worker.taxation || '',
    panNo: worker.panNo || '',
    birthDate: worker.birthDate ? fmtDate(worker.birthDate) : '',
    anniversaryDate: worker.anniversaryDate ? fmtDate(worker.anniversaryDate) : '',
    acType: worker.acType || '',
    acNo: worker.acNo || '',
    ifsc: worker.ifsc || '',
    micr: worker.micr || '',
    bankName: worker.bankName || '',
    bankAdd: worker.bankAdd || '',
    acPerson: worker.acPerson || '',
    acContact: worker.acContact || '',
    acEmail: worker.acEmail || '',
  };
}

const SALARY_TYPES = ['Monthly', 'Daily', 'Piece Rate', 'Hourly'];
const TAXATION_TYPES = ['Regular', 'Composition', 'Unregistered'];
const AC_TYPES = ['Savings', 'Current'];

// Backend WorkerRole enum — required by POST/PUT /api/workers alongside `name`.
const ROLES = [
  { value: 'CUTTING_MASTER', label: 'Cutting Master' },
  { value: 'SEWING_TAILOR', label: 'Sewing Tailor' },
  { value: 'ALL_ROUNDER', label: 'All Rounder' },
  // Supervisors and managers are kept as workers so their login and their pay
  // work like anyone else's, but no garment is ever handed to them.
  { value: 'NO_TRADE', label: 'Other' },
];

function Section({ title, children }) {
  return (
    <div className="border-b border-line pb-5 last:border-b-0 last:pb-0">
      <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-brand-700">{title}</h4>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

/**
 * The garments a worker can make, as a row of tick-on chips.
 *
 * A chip grid rather than a multi-select: a tailor's list is short, and the
 * point is to see at a glance what someone does — which a collapsed select
 * cannot show.
 */
function SkillPicker({ value = [], onChange }) {
  const { data, isLoading } = useQuery({
    queryKey: ['tailoring-items', 'skills'],
    queryFn: () => listItems({ perPage: 200, status: 'ACTIVE' }),
    staleTime: 60_000,
  });
  const items = data?.items || data?.tailoringItems || [];
  const chosen = new Set((value || []).map(Number));

  const toggle = (id) => {
    const next = new Set(chosen);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange([...next]);
  };

  if (isLoading) return <p className="text-xs text-content-subtle">Loading garments…</p>;
  if (!items.length) {
    return <p className="text-xs text-content-subtle">No garments in the catalogue yet — add some under Items.</p>;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => {
          const on = chosen.has(it.id);
          return (
            <button
              key={it.id}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(it.id)}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                on
                  ? 'border-brand-600 bg-brand-600 text-white'
                  : 'border-line-strong bg-surface text-content-muted hover:border-brand-400 hover:text-content'
              }`}
            >
              {it.name}
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 text-2xs text-content-subtle">
        {chosen.size ? `${chosen.size} selected` : 'Nothing selected — they will not be suggested for any garment.'}
      </p>
    </div>
  );
}

export default function WorkerForm({ worker, onSubmit, onCancel, saving, error }) {
  const [form, setForm] = useState(() => workerToFormState(worker));

  const set = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    set(name, type === 'checkbox' ? checked : value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="rounded-lg border border-red-200 bg-danger-50 px-3 py-2 text-sm text-danger-600">{error}</div>
      )}

      <Section title="Worker Details">
        <Field label="Code">
          <input className="input" name="code" value={form.code} onChange={handleChange} />
        </Field>
        <Field label="Name (Ledger Name)" required>
          <input className="input" name="ledgerName" value={form.ledgerName} onChange={handleChange} required />
        </Field>
        <Field label="Grade">
          <input className="input" name="grade" value={form.grade} onChange={handleChange} />
        </Field>
        <Field label="Role" required hint="Cutting or stitching — which kind of work they do">
          <select className="input" name="role" value={form.role} onChange={handleChange} required>
            <option value="">Select role…</option>
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </Field>
        {/* Which garments they can make. Spans the row because the list is
            as long as the shop's catalogue. */}
        <div className="sm:col-span-2 lg:col-span-3">
          <Field
            label="Skills"
            hint="Which garments this person can make. Used when handing work out, and to filter the worker list."
          >
            <SkillPicker
              value={form.skillItemIds}
              onChange={(ids) => set('skillItemIds', ids)}
            />
          </Field>
        </div>
        <Field label="Opening Balance">
          <input className="input" type="number" step="0.01" name="openingBalance" value={form.openingBalance} onChange={handleChange} />
        </Field>
        <label className="flex items-center gap-2 pt-1 text-sm text-content-muted">
          <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} className="h-4 w-4 rounded border-line-strong text-brand-700 focus:ring-brand-400" />
          Is Active
        </label>
      </Section>

      <Section title="Grouping &amp; Contact">
        <Field label="A/c Group">
          <input className="input" name="acGroup" value={form.acGroup} onChange={handleChange} />
        </Field>
        <Field label="Add1">
          <input className="input" name="add1" value={form.add1} onChange={handleChange} />
        </Field>
        <Field label="Add2">
          <input className="input" name="add2" value={form.add2} onChange={handleChange} />
        </Field>
        <Field label="Mob. No.">
          <input className="input" name="mobNo" value={form.mobNo} onChange={handleChange} />
        </Field>
        <Field label="Mob. No. 2">
          <input className="input" name="mobNo2" value={form.mobNo2} onChange={handleChange} />
        </Field>
        <Field label="Ph. No.">
          <input className="input" name="phNo" value={form.phNo} onChange={handleChange} />
        </Field>
        <Field label="E-Mail">
          <input className="input" type="email" name="email" value={form.email} onChange={handleChange} />
        </Field>
        <Field label="City">
          <input className="input" name="city" value={form.city} onChange={handleChange} />
        </Field>
        <Field label="Pincode">
          <input className="input" name="pincode" value={form.pincode} onChange={handleChange} />
        </Field>
        <Field label="State">
          <select className="input" name="state" value={form.state} onChange={handleChange}>
            <option value="">Select state…</option>
            {STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Field>
        <Field label="Country">
          <select className="input" name="country" value={form.country} onChange={handleChange}>
            {COUNTRIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </Field>
      </Section>

      <Section title="Financial &amp; Tax Details">
        <Field label="Salary Type">
          <select className="input" name="salaryType" value={form.salaryType} onChange={handleChange}>
            <option value="">Select…</option>
            {SALARY_TYPES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Field>
        <Field label="VAT No. (GST No.)">
          <input className="input" name="vatNo" value={form.vatNo} onChange={handleChange} />
        </Field>
        <Field label="Taxation">
          <select className="input" name="taxation" value={form.taxation} onChange={handleChange}>
            <option value="">Select…</option>
            {TAXATION_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>
        <Field label="PAN No.">
          <input className="input" name="panNo" value={form.panNo} onChange={handleChange} />
        </Field>
        <Field label="Birth Date">
          <DateInput value={form.birthDate} onChange={(v) => set('birthDate', v)} />
        </Field>
        <Field label="Anniversary Date">
          <DateInput value={form.anniversaryDate} onChange={(v) => set('anniversaryDate', v)} />
        </Field>
      </Section>

      <Section title="Bank Details">
        <Field label="A/c Type">
          <select className="input" name="acType" value={form.acType} onChange={handleChange}>
            <option value="">Select…</option>
            {AC_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>
        <Field label="A/c No.">
          <input className="input" name="acNo" value={form.acNo} onChange={handleChange} />
        </Field>
        <Field label="IFSC">
          <input className="input" name="ifsc" value={form.ifsc} onChange={handleChange} />
        </Field>
        <Field label="MICR">
          <input className="input" name="micr" value={form.micr} onChange={handleChange} />
        </Field>
        <Field label="Bank Name">
          <input className="input" name="bankName" value={form.bankName} onChange={handleChange} />
        </Field>
        <Field label="Bank Add.">
          <input className="input" name="bankAdd" value={form.bankAdd} onChange={handleChange} />
        </Field>
        <Field label="A/c Person">
          <input className="input" name="acPerson" value={form.acPerson} onChange={handleChange} />
        </Field>
        <Field label="A/c Contact">
          <input className="input" name="acContact" value={form.acContact} onChange={handleChange} />
        </Field>
        <Field label="A/c Email">
          <input className="input" type="email" name="acEmail" value={form.acEmail} onChange={handleChange} />
        </Field>
      </Section>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className="btn-outline" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : worker ? 'Update Worker' : 'Add Worker'}
        </button>
      </div>
    </form>
  );
}